import { useEffect, useState } from "react";
import { useIsVerified } from "src/components/email-verification";
import {
  MainCard,
  MainCardContent,
  MainCardTitle,
} from "src/components/main-card-type.tsx";
import { Button } from "src/components/ui/button";
import {
  checkCalculatorUsageLimitAndRedirect,
  loadSavedCalculatorData,
  sanitizeBrlValue,
  saveCalculatorData,
} from "src/utils/functions";
import { calcularSalarioLiquido } from "../utils/salaryFunctions";
import { SalaryInputData, SalaryResultsType, ValidationMessages } from "../salario-liquido";

// Constantes de configuração
const CALCULATOR_KEY = "salario-liquido";
const MAX_DEPENDENTES = 20;
const MIN_DEPENDENTES = 0;

const DEFAULT_VALUES: SalaryInputData = {
  salarioBruto: "",
  numeroDependentes: "0",
  dependentesMenores14: "0",
  pensaoAlimenticia: "",
  outrosDescontos: "",
} as const;

// Funções utilitárias extraídas para evitar duplicação
const parseNumericValue = (value: string): number => {
  return Number.parseFloat(value.replace(/[^0-9,-]+/g, "").replace(",", ".")) || 0;
};

const parseIntegerValue = (value: string): number => {
  return Math.max(0, Number.parseInt(value) || 0);
};

export const SalaryInputCard = ({
  setResults,
}: {
  setResults: (value: SalaryResultsType | null) => void;
}) => {
  const [message, setMessage] = useState<string>("");
  const [formData, setFormData] = useState<SalaryInputData>(DEFAULT_VALUES);
  const [isCalculating, setIsCalculating] = useState(false);

  // REGRA DE NEGÓCIO: Verificação de usuário
  const isVerified = useIsVerified();

  const updateFormField = <K extends keyof SalaryInputData>(field: K, value: SalaryInputData[K]) => {
    setFormData((prev: SalaryInputData) => ({ ...prev, [field]: value }));
  };

  // Validação simplificada e quebrada em funções menores
  const validateSalario = (salario: string): string | null => {
    if (!salario) return ValidationMessages.SALARIO_OBRIGATORIO;

    const salarioNum = parseNumericValue(salario);
    if (salarioNum <= 0) return ValidationMessages.SALARIO_INVALIDO;

    return null;
  };

  const validateDependentes = (dependentes: string, dependentesMenores: string): string | null => {
    const dependentesNum = parseIntegerValue(dependentes);
    const dependentesMenoresNum = parseIntegerValue(dependentesMenores);

    if (dependentesMenoresNum > dependentesNum) {
      return ValidationMessages.DEPENDENTES_INVALIDOS;
    }

    return null;
  };

  const validateForm = (): string | null => {
    return validateSalario(formData.salarioBruto) ||
      validateDependentes(formData.numeroDependentes, formData.dependentesMenores14);
  };

  // Parsing extraído para função utilitária
  const parseFormData = () => ({
    salarioBruto: parseNumericValue(formData.salarioBruto),
    dependentes: parseIntegerValue(formData.numeroDependentes),
    dependentesMenores14: parseIntegerValue(formData.dependentesMenores14),
    pensao: parseNumericValue(formData.pensaoAlimenticia),
    outros: parseNumericValue(formData.outrosDescontos),
  });

  // Cálculo simplificado
  const executeCalculation = async () => {
    const { salarioBruto, dependentes, dependentesMenores14, pensao, outros } = parseFormData();

    const resultado = calcularSalarioLiquido(
      salarioBruto,
      dependentes,
      dependentesMenores14,
      pensao,
      outros
    );

    setResults(resultado);
    setMessage("");
  };

  // Função principal quebrada em etapas menores
  const calcular = async () => {
    const validationError = validateForm();
    if (validationError) {
      setMessage(validationError);
      return;
    }

    setIsCalculating(true);
    setMessage("");

    try {
      await executeCalculation();
    } catch (error) {
      console.error('Erro no cálculo:', error);
      setMessage((error as Error).message || ValidationMessages.ERRO_CALCULO);
      setResults(null);
    } finally {
      setIsCalculating(false);
    }
  };

  const clear = () => {
    setFormData(DEFAULT_VALUES);
    setResults(null);
    setMessage("");
  };

  const handleSave = () => {
    saveCalculatorData(CALCULATOR_KEY, {
      salarioBruto: formData.salarioBruto,
      numeroDependentes: formData.numeroDependentes,
      dependentesMenores14: formData.dependentesMenores14,
      pensaoAlimenticia: formData.pensaoAlimenticia,
      outrosDescontos: formData.outrosDescontos,
    });
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    handleSave();
    checkCalculatorUsageLimitAndRedirect(calcular);
  };

  useEffect(() => {
    const savedData = loadSavedCalculatorData(CALCULATOR_KEY);
    if (savedData) {
      setFormData({
        salarioBruto: (savedData.salarioBruto as string) || DEFAULT_VALUES.salarioBruto,
        numeroDependentes: (savedData.numeroDependentes as string) || DEFAULT_VALUES.numeroDependentes,
        dependentesMenores14: (savedData.dependentesMenores14 as string) || DEFAULT_VALUES.dependentesMenores14,
        pensaoAlimenticia: (savedData.pensaoAlimenticia as string) || DEFAULT_VALUES.pensaoAlimenticia,
        outrosDescontos: (savedData.outrosDescontos as string) || DEFAULT_VALUES.outrosDescontos,
      });
    }
  }, []);

  return (
    <MainCard>
      <MainCardTitle>Calculadora de Salário Líquido</MainCardTitle>
      <form onSubmit={handleSubmit}>
        <MainCardContent>
          <div>
            <label className="block mb-2 font-medium text-sm" htmlFor="salario-bruto">
              Salário Bruto *
            </label>
            <div className="flex border">
              <span className="inline-flex items-center px-3 border-r text-sm bg-[#e9ecef]">
                R$
              </span>
              <input
                className="flex-1 p-2 min-w-[100px]"
                id="salario-bruto"
                placeholder="0,00"
                type="text"
                value={formData.salarioBruto}
                onChange={(event) => updateFormField('salarioBruto', sanitizeBrlValue(event))}
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block mb-2 font-medium text-sm" htmlFor="numero-dependentes">
                Número de Dependentes
              </label>
              <input
                className="w-full p-2 border rounded"
                id="numero-dependentes"
                placeholder="0"
                type="number"
                min={MIN_DEPENDENTES}
                max={MAX_DEPENDENTES}
                value={formData.numeroDependentes}
                onChange={(event) => updateFormField('numeroDependentes', event.target.value)}
              />
            </div>
            <div>
              <label className="block mb-2 font-medium text-sm" htmlFor="dependentes-menores-14">
                Nº de menores de 14 anos
              </label>
              <input
                className="w-full p-2 border rounded"
                id="dependentes-menores-14"
                placeholder="0"
                type="number"
                min={MIN_DEPENDENTES}
                max={MAX_DEPENDENTES}
                value={formData.dependentesMenores14}
                onChange={(event) => updateFormField('dependentesMenores14', event.target.value)}
              />
              <small className="text-xs text-gray-600 mt-1 block">
                Para cálculo do salário-família (renda até R$ 1.906,04)
              </small>
            </div>
          </div>

          <div className="bg-[#e9ecef] p-4 rounded border">
            <h3 className="text-sm font-semibold mb-3 text-gray-700">
              Outros Descontos (opcional)
            </h3>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block mb-2 font-medium text-sm" htmlFor="pensao-alimenticia">
                  Pensão Alimentícia
                </label>
                <div className="flex border">
                  <span className="inline-flex items-center px-3 border-r text-sm bg-[#e9ecef]">
                    R$
                  </span>
                  <input
                    className="flex-1 p-2 min-w-[100px]"
                    id="pensao-alimenticia"
                    placeholder="0,00"
                    type="text"
                    value={formData.pensaoAlimenticia}
                    onChange={(event) => updateFormField('pensaoAlimenticia', sanitizeBrlValue(event))}
                  />
                </div>
              </div>

              <div>
                <label className="block mb-2 font-medium text-sm" htmlFor="outros-descontos">
                  Outros Descontos
                </label>
                <div className="flex border">
                  <span className="inline-flex items-center px-3 border-r text-sm bg-[#e9ecef]">
                    R$
                  </span>
                  <input
                    className="flex-1 p-2 min-w-[100px]"
                    id="outros-descontos"
                    placeholder="0,00"
                    type="text"
                    value={formData.outrosDescontos}
                    onChange={(event) => updateFormField('outrosDescontos', sanitizeBrlValue(event))}
                  />
                </div>
              </div>
            </div>
          </div>

          <div className="bg-[#e9ecef] p-4 rounded border">
            <h3 className="text-sm font-semibold mb-3 text-gray-700">
              Atualização 2025
            </h3>
            <p className="text-xs text-gray-700">
              Cálculos atualizados com as tabelas oficiais 2025: INSS, IRRF e salário-família conforme Portaria MPS/MF nº 6/2025.
            </p>
          </div>
        </MainCardContent>

        <div className="flex justify-between">
          <Button
            disabled={!isVerified || isCalculating}
            type="submit"
            variant="default"
            className="sm:w-[150px]"
          >
            {isCalculating ? (
              <>
                <span className="animate-spin mr-2">⏳</span>
                Calculando...
              </>
            ) : (
              "Calcular"
            )}
          </Button>
          <Button onClick={clear} variant="ghost" type="button">
            Limpar
          </Button>
        </div>
      </form>

      <p className="text-red-800 text-center mt-2 font-bold">{message}</p>
    </MainCard>
  );
};